import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Info, Pencil, TriangleAlert } from "lucide-react"

import { Chart } from "@/components/charts/Chart"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount } from "@/lib/format"
import { t } from "@/lib/i18n"
import { cn } from "@/lib/utils"
import { nombreCuenta } from "@/routes/finance/mapping"

import {
  AjustarCeldaDialog,
  type CeldaProyectada,
} from "@/routes/forecast/AjustarCeldaDialog"
import {
  fetchCajaProyectada,
  fetchCobranzaProyectada,
  type AjusteDeCelda,
  type BloqueAjustable,
  type IdEscenario,
  type Proyeccion,
} from "@/routes/forecast/api"

const MES_CORTO = ["Ene", "Feb", "Mar", "Abr", "May", "Jun",
                   "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"]
const MES_LARGO = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
                   "julio", "agosto", "septiembre", "octubre", "noviembre",
                   "diciembre"]

function serie(meses: Record<string, number> | undefined): number[] {
  return Array.from({ length: 12 }, (_, i) => Number(meses?.[String(i + 1)] ?? 0))
}

// ===========================================================================
// LA CAJA PROYECTADA
// ===========================================================================

/**
 * QUÉ QUEDA EN EL BANCO CADA MES.
 *
 * Cesar: «parece que sólo has proyectado ingresos y eso no está bien». El P&L
 * proyectado dice cuánto se factura; esto dice si alcanza.
 *
 * LO PRIMERO QUE SE DICE ES EL MES EN QUE LA CAJA CRUZA EL CERO, y lo calcula
 * el servidor. El neto de un mes malo no asusta a nadie; el mes en que el
 * acumulado se pone en rojo es la única cifra de esta pantalla por la que
 * alguien va a hacer algo esta semana.
 */
export function CajaProyectadaPanel({
  anio, escenario, pais, ajustable,
}: {
  anio: number
  escenario: IdEscenario
  pais?: string
  ajustable: boolean
}) {
  const { activeTenantId } = useAuth()
  const [celda, setCelda] = React.useState<CeldaProyectada | null>(null)

  const q = useQuery({
    queryKey: ["forecast-caja", activeTenantId, anio, pais, escenario],
    queryFn: () => fetchCajaProyectada(anio, escenario, pais),
    enabled: Boolean(activeTenantId),
  })

  const d = q.data
  const corte = d?.corte?.mes ?? null
  const negativos = d?.meses_en_negativo ?? []

  return (
    <>
      <WorkPanel
        title={t("La caja, mes a mes")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {d?.escenario.nombre ?? escenario} · USD
          </span>
        }
        footnote={d?.nota}
      >
        <DataState
          loading={q.isLoading}
          error={q.error}
          empty={!q.isLoading && !(d?.cuentas ?? []).length}
          emptyMessage={`No hay modelo de caja para ${anio}.`}
          onRetry={() => void q.refetch()}
          rows={5}
        >
          {/*
            EL AVISO VA ARRIBA Y NO AL PIE. Un acumulado que se pone en rojo en
            noviembre y nadie ve hasta diciembre es exactamente el dato que hace
            decidir tarde.
          */}
          {d?.aviso ? (
            <p className="flex items-start gap-2 border-b bg-destructive/5 px-2.5 py-2 text-xs leading-5 text-destructive">
              <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {d.aviso}
            </p>
          ) : null}

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[12rem]">{t("Concepto")}</TableHead>
                  {MES_CORTO.map((m, i) => (
                    <TableHead
                      key={m}
                      className={cn("w-24 text-right",
                        corte !== null && i + 1 === corte && "corte-real")}
                    >
                      {m}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Año")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(d?.cuentas ?? []).map((c) => (
                  <TableRow key={c.account}>
                    <TableCell>
                      {nombreCuenta(c.account)}
                      {c.elasticidad === 0 ? (
                        <Badge variant="outline" className="ml-1.5">{t("fija")}</Badge>
                      ) : null}
                    </TableCell>
                    {serie(c.meses).map((v, i) => (
                      <CeldaEditable
                        key={i}
                        valor={v}
                        mes={i + 1}
                        real={corte !== null && i + 1 <= corte}
                        enElCorte={corte !== null && i + 1 === corte}
                        ajustada={c.meses_ajustados.includes(i + 1)}
                        puedeEditar={ajustable}
                        onEditar={() => setCelda({
                          pais: pais!, anio, mes: i + 1, bloque: "cashflow",
                          cuenta: c.account, proyectado: v,
                          ajuste: ajusteDe(d?.ajustes, "cashflow", c.account, i + 1),
                          escenarioActual: escenario,
                        })}
                      />
                    ))}
                    <TableCell className="num font-medium">
                      {formatAmount(c.total)}
                    </TableCell>
                  </TableRow>
                ))}

                <FilaResumen etiqueta={t("Neto del mes")} valores={serie(d?.neto)} corte={corte} />
                <FilaResumen
                  etiqueta={t("Caja acumulada")}
                  valores={serie(d?.acumulado)}
                  destacar
                  /*
                    TODOS LOS MESES EN ROJO van marcados en la fila, no sólo el
                    primero: quien recorre la tabla con el dedo tiene que poder
                    ver el tramo entero sin haber leído la cabecera, y ver que
                    hay un mes bueno en medio cambia la decisión.
                  */
                  marcar={negativos}
                  corte={corte}
                />
              </TableBody>
            </Table>
          </div>

          {/*
            LAS DOS CIFRAS JUNTAS. El acumulado del modelo NO es el saldo de los
            bancos, y presentarlo solo lo hace pasar por lo que no es.
          */}
          {d ? (
            <div className="flex flex-wrap gap-x-4 gap-y-1 border-t px-2.5 py-2 text-2xs text-muted-foreground">
              <span>
                {t("Saldo inicial")}: <span className="num">{formatAmount(d.saldo_inicial)}</span>
              </span>
              {d.caja_bancos !== null ? (
                <span>
                  {t("Bancos hoy")}: <span className="num">{formatAmount(d.caja_bancos)}</span>
                </span>
              ) : null}
              {d.acumula_desde ? (
                <span>{t("Acumula desde")} {d.acumula_desde}</span>
              ) : null}
            </div>
          ) : null}

          <ListaDeAjustes ajustes={d?.ajustes ?? []} />
        </DataState>
      </WorkPanel>

      <AjustarCeldaDialog
        celda={celda}
        open={celda !== null}
        onOpenChange={(v) => { if (!v) setCelda(null) }}
      />
    </>
  )
}

// ===========================================================================
// LOS COBROS
// ===========================================================================

/**
 * CUÁNDO ENTRA LA PLATA.
 *
 * DOS SERIES Y NUNCA UNA SOLA. `de_cartera` es lo ya emitido —un hecho— y
 * `de_lo_nuevo` es lo que se facturará —una expectativa—. Presentar la suma
 * sin la proporción mezcla las dos cosas, y quien mire el total para decidir
 * si aguanta el mes estaría apostando sin saberlo.
 */
export function CobranzaProyectadaPanel({
  anio, escenario, pais,
}: {
  anio: number
  escenario: IdEscenario
  pais?: string
}) {
  const { activeTenantId } = useAuth()
  const q = useQuery({
    queryKey: ["forecast-cobranza", activeTenantId, anio, pais, escenario],
    queryFn: () => fetchCobranzaProyectada(anio, escenario, pais),
    enabled: Boolean(activeTenantId),
  })

  const d = q.data
  const cartera = d?.cartera
  /*
    EL CORTE TAMBIÉN AQUÍ. `/finance/forecast/cobranza` lo devuelve y este panel
    no lo usaba: la tabla de cobros salía sin la línea mientras la de caja, justo
    encima, sí la tenía. Dos tablas de la misma pantalla diciendo cosas distintas
    sobre dónde acaba lo real.
  */
  const corte = d?.corte?.mes ?? null

  return (
    <WorkPanel
      title={t("Cuándo entra la plata")}
      meta={
        d ? (
          <span className="text-2xs text-muted-foreground">
            {d.escenario.nombre}{" · "}
            {t("se cobra a los {n} días del vencimiento")
              .replace("{n}", String(d.retraso_dias))}
          </span>
        ) : null
      }
      footnote={t("El escenario malo de cobranza no es «cobras menos»: es «cobras más tarde». El dinero acaba entrando; lo que duele mientras tanto es el hueco.")}
    >
      <DataState
        loading={q.isLoading}
        error={q.error}
        empty={!q.isLoading && !cartera?.facturas}
        emptyMessage={t("No hay cartera por cobrar.")}
        onRetry={() => void q.refetch()}
        rows={4}
      >
        {cartera ? (
          <MetricRow className="border-b p-2.5 lg:grid-cols-4">
            <MetricCard label={t("Cartera abierta")} value={cartera.importe_usd} />
            <MetricCard
              label={t("Entra este año")}
              value={cartera.cobrable_en_el_anio_usd}
              tono="destacado"
            />
            <MetricCard label={t("En gestión")} value={cartera.en_gestion_usd} />
            <MetricCard
              label={t("Dado por incobrable")}
              value={cartera.declarado_incobrable_usd}
              tono={cartera.declarado_incobrable_usd > 0 ? "alerta" : "neutro"}
            />
          </MetricRow>
        ) : null}

        <div className="p-2.5">
          <Chart
            etiquetas={MES_CORTO}
            tipo="barras"
            series={[
              { nombre: t("De la cartera ya emitida"), valores: serie(d?.de_cartera) },
              { nombre: t("De lo que aún no se factura"), valores: serie(d?.de_lo_nuevo) },
            ]}
          />
        </div>

        <div className="overflow-x-auto border-t">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[14rem]">{t("Origen del cobro")}</TableHead>
                {MES_CORTO.map((m, i) => (
                  <TableHead
                    key={m}
                    className={cn("w-24 text-right",
                                  corte !== null && i + 1 === corte && "corte-real")}
                  >
                    {m}
                  </TableHead>
                ))}
                <TableHead className="w-28 text-right">{t("Año")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <FilaDeCobros
                etiqueta={t("Cartera ya emitida")}
                ayuda={t("Facturas que ya existen y no se han cobrado. Es un hecho.")}
                valores={serie(d?.de_cartera)}
                corte={corte}
              />
              <FilaDeCobros
                etiqueta={t("Lo que aún no se factura")}
                ayuda={t("Sale de las ventas proyectadas, desplazadas los meses que se tarda en cobrar. Es una expectativa.")}
                valores={serie(d?.de_lo_nuevo)}
                corte={corte}
              />
              <FilaResumen etiqueta={t("Total que entra")} valores={serie(d?.total)} destacar corte={corte} />
            </TableBody>
          </Table>
        </div>

        {/*
          LO QUE LA CIFRA NO DICE DE SÍ MISMA, arriba de la nota al pie y no
          dentro de ella: una cartera que no se puede fechar no está perdida,
          está sin proyectar, y desaparecer en silencio es lo que hace que el
          modelo deje de esperar plata sin avisar a nadie.
        */}
        {(d?.avisos ?? []).map((a, i) => (
          <p key={i} className="flex gap-1.5 border-t px-2.5 py-1.5 text-2xs leading-4 text-muted-foreground">
            <Info className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            {a}
          </p>
        ))}

        {cartera?.sin_fecha_para_proyectar.length ? (
          <details className="border-t px-2.5 py-1.5 text-2xs">
            <summary className="cursor-pointer text-muted-foreground">
              {t("Ver las facturas sin fecha de emisión")}{" "}
              ({cartera.sin_fecha_para_proyectar.length})
            </summary>
            <ul className="mt-1 space-y-0.5">
              {cartera.sin_fecha_para_proyectar.map((f, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span>{f.numero || t("(sin número)")} · {f.cliente}</span>
                  <span className="num">{formatAmount(f.importe_usd)}</span>
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </DataState>
    </WorkPanel>
  )
}

// ===========================================================================
// AJUSTAR EL P&L PROYECTADO, mes a mes
// ===========================================================================

/**
 * LA REJILLA EDITABLE: cuenta × mes, para el escenario que se esté mirando.
 *
 * La tabla de los tres escenarios enseña TOTALES del año, y un total no se
 * puede pisar: «Revenue 2026 = 600.000» no dice en qué mes entra, y la caja
 * —que es lo que se está tratando de ver— depende exactamente de eso.
 *
 * Los meses anteriores al corte salen en gris y no se pueden tocar: ya tienen
 * asientos en el libro. El servidor también los rechaza; aquí se desactivan
 * para que nadie escriba un número que va a perder.
 */
export function AjustarProyeccionPanel({
  anio, pais, base, escenario, ajustable, noAjustablePorque,
}: {
  anio: number
  pais?: string
  base: Proyeccion | undefined
  escenario: IdEscenario
  ajustable: boolean
  noAjustablePorque: string | null
}) {
  const [celda, setCelda] = React.useState<CeldaProyectada | null>(null)
  const corte = base?.corte?.mes ?? null

  return (
    <>
      <WorkPanel
        title={t("Ajusta la proyección")}
        meta={
          <span className="text-2xs text-muted-foreground">
            {base?.escenario.nombre ?? escenario}{" · "}{anio}
          </span>
        }
        footnote={
          ajustable
            ? t("Toca un mes posterior a la línea roja para escribir lo que sabes que va a pasar. Tu número no se vuelve a escalar con el escenario.")
            : noAjustablePorque
        }
      >
        <DataState
          loading={!base}
          empty={Boolean(base) && !base?.cuentas.length}
          emptyMessage={`No hay modelo financiero para ${anio}.`}
          rows={5}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[12rem]">{t("Concepto")}</TableHead>
                  {MES_CORTO.map((m, i) => (
                    <TableHead
                      key={m}
                      className={cn("w-24 text-right",
                        corte !== null && i + 1 === corte && "corte-real")}
                    >
                      {m}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Año")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(base?.cuentas ?? []).map((c) => {
                  const ajustados =
                    (c as { meses_ajustados?: number[] }).meses_ajustados ?? []
                  return (
                    <TableRow key={c.account}>
                      <TableCell>{nombreCuenta(c.account)}</TableCell>
                      {Array.from({ length: 12 }, (_, i) =>
                        Number(c.meses?.[String(i + 1)] ?? 0)).map((v, i) => (
                        <CeldaEditable
                          key={i}
                          valor={v}
                          mes={i + 1}
                          real={corte !== null && i + 1 <= corte}
                        enElCorte={corte !== null && i + 1 === corte}
                          ajustada={ajustados.includes(i + 1)}
                          puedeEditar={ajustable}
                          onEditar={() => setCelda({
                            pais: pais!, anio, mes: i + 1,
                            bloque: "income_statement", cuenta: c.account,
                            proyectado: v,
                            ajuste: ajusteDe(base?.ajustes, "income_statement",
                                             c.account, i + 1),
                            escenarioActual: escenario,
                          })}
                        />
                      ))}
                      <TableCell className="num font-medium">
                        {formatAmount(c.total)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>

          <ListaDeAjustes ajustes={base?.ajustes ?? []} />
        </DataState>
      </WorkPanel>

      <AjustarCeldaDialog
        celda={celda}
        open={celda !== null}
        onOpenChange={(v) => { if (!v) setCelda(null) }}
      />
    </>
  )
}

// ===========================================================================
// Piezas compartidas
// ===========================================================================

function ajusteDe(ajustes: AjusteDeCelda[] | undefined, bloque: BloqueAjustable,
                  cuenta: string, mes: number) {
  const a = (ajustes ?? []).find(
    (x) => x.bloque === bloque && x.cuenta === cuenta && x.mes === mes)
  return a ? { valor_usd: a.valor_usd, motivo: a.motivo, escenario: a.escenario } : null
}

/**
 * Una celda de la rejilla.
 *
 * TRES ESTADOS Y NO DOS: real (ya pasó, no se toca), proyectada (se puede
 * pisar) y ajustada (alguien ya la pisó).
 *
 * LA AJUSTADA YA NO VA RESALTADA. Cesar, 2026-10-04: «has dejado marcado en
 * forecast diciembre como editando, debería de ser como el resto que recién se
 * marque cuando le doy click». Llevaba fondo verde y lápiz fijos, y se leía
 * como una celda a medio editar. Ahora se ve como las demás; que fue escrita a
 * mano se sabe al pasar el cursor (el título lo dice) y en la lista «Celdas que
 * escribiste tú», debajo de la tabla, que es la que deja constancia.
 */
function CeldaEditable({
  valor, mes, real, ajustada, puedeEditar, onEditar, enElCorte,
}: {
  valor: number
  mes: number
  real: boolean
  ajustada: boolean
  puedeEditar: boolean
  onEditar: () => void
  /**
   * ¿Es la columna donde termina lo real?
   *
   * Cesar: «en caja mes a mes, ajusta la proyección en forecast en oct no está
   * la línea roja». Y no estaba: estos paneles marcaban el corte con
   * `border-r-2 border-brand` —el VERDE de la marca— y **sólo en la fila de
   * cabecera**. Un trazo de 2px en una celda de encabezado no se lee como una
   * línea que separa dos mitades de la tabla; se lee como un borde.
   *
   * Ahora usan `.corte-real`, que es la misma clase del P&L, de la caja y del
   * desglose, y va en TODAS las celdas de la columna. Dos formas de pintar lo
   * mismo en pantallas que se comparan es cómo alguien acaba creyendo que el
   * corte está en sitios distintos.
   */
  enElCorte?: boolean
}) {
  const editable = puedeEditar && !real
  const contenido = <>{formatAmount(valor)}</>

  if (!editable) {
    return (
      <TableCell
        className={cn("num", real && "text-muted-foreground",
                      valor < 0 && "text-destructive",
                      enElCorte && "corte-real")}
        title={real ? t("Ya tiene asientos en el libro: esto no se proyecta, pasó.") : undefined}
      >
        {contenido}
      </TableCell>
    )
  }

  return (
    <TableCell className={cn("num p-0", valor < 0 && "text-destructive",
                             enElCorte && "corte-real")}>
      <button
        type="button"
        onClick={onEditar}
        title={ajustada
          ? t("Ajustada a mano. Toca para cambiarla o quitarla.")
          : `${t("Ajustar")} ${MES_LARGO[mes - 1]}`}
        className="h-full w-full px-2 py-1 text-right transition-colors hover:bg-brand-soft"
      >
        {contenido}
      </button>
    </TableCell>
  )
}

function FilaResumen({
  etiqueta, valores, destacar, marcar, corte,
}: {
  etiqueta: string
  valores: number[]
  destacar?: boolean
  marcar?: number[]
  /**
   * El mes donde termina lo real.
   *
   * LA LÍNEA TIENE QUE PASAR TAMBIÉN POR AQUÍ. «Neto» y «Acumulado» son las
   * filas que de verdad se miran en una tabla de caja, y sin la marca la raya
   * se cortaba justo antes de ellas — que es como si no estuviera: el ojo la
   * busca precisamente en el renglón del total.
   */
  corte?: number | null
}) {
  return (
    <TableRow className={cn("font-semibold",
      destacar ? "bg-brand-soft" : "bg-surface-sunken/60")}>
      <TableCell>{etiqueta}</TableCell>
      {valores.map((v, i) => (
        <TableCell
          key={i}
          className={cn("num", v < 0 && "text-destructive",
            marcar?.includes(i + 1) && "ring-1 ring-inset ring-destructive",
            corte != null && i + 1 === corte && "corte-real")}
        >
          {formatAmount(v)}
        </TableCell>
      ))}
      <TableCell className="num">
        {formatAmount(valores.reduce((a, b) => a + b, 0))}
      </TableCell>
    </TableRow>
  )
}

function FilaDeCobros({
  etiqueta, ayuda, valores, corte,
}: {
  etiqueta: string
  ayuda: string
  valores: number[]
  /** Ver `FilaResumen`: la línea tiene que cruzar la tabla entera. */
  corte?: number | null
}) {
  return (
    <TableRow>
      <TableCell>
        {etiqueta}
        <p className="text-2xs leading-4 text-muted-foreground">{ayuda}</p>
      </TableCell>
      {valores.map((v, i) => (
        <TableCell key={i}
                   className={cn("num", corte != null && i + 1 === corte && "corte-real")}>
          {formatAmount(v)}
        </TableCell>
      ))}
      <TableCell className="num font-medium">
        {formatAmount(valores.reduce((a, b) => a + b, 0))}
      </TableCell>
    </TableRow>
  )
}

/**
 * LOS AJUSTES QUE ENTRARON EN ESTA CIFRA, con su motivo.
 *
 * Una proyección con celdas pisadas a mano que no las declara es un número que
 * parece derivado y no lo es. Va al pie de la tabla, donde está la cifra, y no
 * en otra pantalla.
 */
function ListaDeAjustes({ ajustes }: { ajustes: AjusteDeCelda[] }) {
  if (!ajustes.length) return null
  return (
    <div className="space-y-0.5 border-t px-2.5 py-2">
      <p className="text-2xs font-medium">
        {t("Celdas que escribiste tú")} ({ajustes.length})
      </p>
      {ajustes.map((a, i) => (
        <p key={i} className="text-2xs leading-4 text-muted-foreground">
          <span className="text-foreground">
            {nombreCuenta(a.cuenta)} · {MES_LARGO[a.mes - 1]}
          </span>{" "}
          <span className="num">{formatAmount(a.valor_usd)}</span>
          {a.revierte_a !== null ? (
            <> ({t("la proyección daba")} <span className="num">{formatAmount(a.revierte_a)}</span>)</>
          ) : null}
          {" — "}{a.motivo}
        </p>
      ))}
    </div>
  )
}
